/**
 * Recherche juridique — espace Avocat (chantier Recherche-Avocats).
 *
 * Page DÉDIÉE (étanchéité des espaces) réutilisant l'API publique `askJurisIA`
 * (même endpoint que l'espace arbitre). Le corpus interrogé est PUBLIC ; aucune
 * donnée de dossier n'est exposée, aucun outil juridictionnel n'est accessible
 * ici. Accessible à tout avocat (fonctionnalité généralisée, plus de flag).
 */
import { useState } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { ArrowLeft, Search, Loader2 } from "lucide-react";

import { askJurisIA, type RechercheResponse } from "@/lib/api";

const GOLD = "oklch(0.695 0.105 68)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";

export default function RechercheAvocat() {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<RechercheResponse | null>(null);

  async function onAsk() {
    const q = question.trim();
    if (q.length < 3) {
      toast.error("Posez une question d'au moins 3 caractères.");
      return;
    }
    setBusy(true);
    try {
      const data = await askJurisIA(q);
      setRes(data);
    } catch {
      toast.error("La recherche est momentanément indisponible.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <Link href="/avocat" className="inline-flex items-center gap-1 text-sm opacity-80">
        <ArrowLeft size={14} /> Retour à l'espace avocat
      </Link>
      <h1 className="mt-2 text-xl font-semibold">Recherche juridique</h1>
      <p className="mt-1 text-sm opacity-70">
        Interrogez le corpus juridique de référence (OHADA). Réponses produites
        avec l'assistance d'un système d'IA — à vérifier avant tout usage.
      </p>

      <div className="mt-4 space-y-3 rounded-md border p-4" style={{ borderColor: BORDER }}>
        <textarea
          className="block w-full rounded-md border px-2 py-1.5 text-sm bg-transparent"
          style={{ borderColor: BORDER }}
          rows={3}
          placeholder="Ex. : Quelles sont les conditions de validité d'une convention d'arbitrage en droit OHADA ?"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <button
          type="button"
          disabled={busy}
          onClick={onAsk}
          className="inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-black disabled:opacity-50"
          style={{ background: GOLD }}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Rechercher
        </button>
      </div>

      {res && (
        <div className="mt-4 space-y-4 rounded-md border p-4" style={{ borderColor: BORDER }}>
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{res.answer}</div>

          {res.sources_used.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide" style={{ color: GOLD }}>
                Sources
              </div>
              <ul className="mt-1 space-y-1 text-sm">
                {res.sources_used.map((s) => (
                  <li key={s.id}>
                    <Link href={`/corpus/${s.id}`} style={{ color: GOLD }}>
                      {s.source}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[11px] opacity-60">
            Système d'IA d'assistance (AI Act, art. 50). Aucune décision
            juridictionnelle n'est produite ici.
          </p>
        </div>
      )}
    </div>
  );
}
